/**
 * The last few groupers assigned through the plan editor's grouping picker,
 * per organization (spec 2026-09-06-06). Filing a whole building goes frame
 * by frame — one unit gets picked for room after room — so the picker's
 * "Recent" group needs to survive a reload and a move to another floor of
 * the same building, which is exactly what `localStorage` gives for free.
 *
 * A `pending:<key>` uid (a grouper created this session, in `SpaceGrouperField`,
 * and not yet saved) may pass through here and show up in the recent group
 * while it exists, but its key means nothing after a reload — only real asset
 * uids are ever written to storage.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/** How many distinct groupers the picker keeps within reach. */
export const RECENT_GROUPERS_MAX = 3

const storageKey = (org: string): string => `plan-editor:recent-groupers:${org}`

/** A `pending:` uid is session-local — only a real asset uid survives a reload. */
export const isPersistableGrouperUid = (uid: string): boolean => !uid.startsWith('pending:')

/** The subset of `uids` worth writing to storage — pending ones dropped. */
export function persistableGrouperUids(uids: string[]): string[] {
    return uids.filter(isPersistableGrouperUid)
}

/** Most-recent first, distinct, capped — the oldest falls off the end. */
export function rememberGrouper(recent: string[], uid: string): string[] {
    return [uid, ...recent.filter((u) => u !== uid)].slice(0, RECENT_GROUPERS_MAX)
}

/**
 * The remembered uids that still resolve to an offered option, in recency
 * order — a lot deleted, archived, or out of this frame's scope is silently
 * skipped rather than shown stale (spec §4).
 */
export function visibleRecentGroupers<T extends { uid: string }>(recentUids: string[], options: T[]): T[] {
    const byUid = new Map(options.map((o) => [o.uid, o]))
    const out: T[] = []
    for (const uid of recentUids) {
        const option = byUid.get(uid)
        if (option) out.push(option)
    }
    return out
}

function storedRecent(org: string): string[] {
    try {
        const raw = localStorage.getItem(storageKey(org))
        const parsed: unknown = raw ? JSON.parse(raw) : null
        if (!Array.isArray(parsed)) return []
        return persistableGrouperUids(parsed.filter((v): v is string => typeof v === 'string'))
            .slice(0, RECENT_GROUPERS_MAX)
    } catch {
        // localStorage may be unavailable in sandboxed contexts.
        return []
    }
}

function storeRecent(org: string, recent: string[]): void {
    try {
        localStorage.setItem(storageKey(org), JSON.stringify(persistableGrouperUids(recent)))
    } catch {
        // Storage is a convenience; the in-memory list still applies to this session.
    }
}

export interface RecentGroupersMemory {
    /** Most recently assigned first, capped at three — may include pending uids. */
    recentUids: string[]
    /** Record a pick — an assignment or an inline creation. Moves it to the front. */
    onPicked: (uid: string) => void
}

export function useRecentGroupers(org: string): RecentGroupersMemory {
    const [recentUids, setRecentUids] = useState<string[]>(() => storedRecent(org))

    // The route reuses this component across frames and organizations, so the
    // memory has to follow `org`, not the mount.
    const loadedFor = useRef(org)
    useEffect(() => {
        if (loadedFor.current === org) return
        loadedFor.current = org
        setRecentUids(storedRecent(org))
    }, [org])

    const onPicked = useCallback((uid: string) => {
        if (!uid) return
        setRecentUids((prev) => {
            const next = rememberGrouper(prev, uid)
            storeRecent(org, next)
            return next
        })
    }, [org])

    return { recentUids, onPicked }
}
